import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { HelpButton } from '@/components/ui/help-button';
import { fieldDescriptions } from '../utils/loadDescriptions';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { useShortTurnConfig } from '../ShortTurnConfigForm';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { cn } from '@/lib/utils';
import { RestGapComparison, type RestGapComparisonProps } from '@/components/config_forms/shared/help-graphics/RestGapComparison';

export const MinimumRestPeriodField: React.FC = () => {
  const { defaultBreakInputProps, customizedFields = {}, context = 'user', markdownVariables, onShortTurnConfigChange } = useShortTurnConfig();
  const labels = {
    user: {
      label: "Minimum Rest Period",
      description: "The minimum amount of rest time you are entitled to between work days for a single client."
    },
    client: {
      label: "Minimum Rest Period",
      description: "Shifts with less than this many hours between them will trigger a short turn surcharge for this client"
    }
  };

  const currentLabels = labels[context];

  const restGapData = markdownVariables.rest_gap_data as RestGapComparisonProps;
  const restGapInjectable = useMemo(() => ({
    rest_gap: <RestGapComparison
      scenarios={restGapData.scenarios}
      minimumRestHours={restGapData.minimumRestHours}
      minimumRestInputValue={restGapData.minimumRestInputValue}
      onMinimumRestChange={(v) => onShortTurnConfigChange({ requiredBreakHours: v })}
    />
  }), [restGapData, onShortTurnConfigChange]);

  return (
    <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:items-center md:gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Label htmlFor="defaultBreak" className={cn(fieldLabelClass, "cursor-pointer whitespace-nowrap")}>
            {currentLabels.label}
          </Label>
          <CustomizedFieldIndicator isCustomized={customizedFields.required_break_hours} context={context} />
          <HelpButton
            fieldName="Short Turn - Minimum Rest Period"
            content={fieldDescriptions.minimum_rest_period}
            variables={markdownVariables}
            enableFieldFocus
            injectableComponents={restGapInjectable}
          />
        </div>
        <p className={cn(fieldDescriptionClass, "mt-1 hidden md:block md:max-w-[44ch]")}>
          {currentLabels.description}
        </p>
      </div>
      <div className="flex justify-end md:items-end">
        <NumberInput
          id="defaultBreak"
          aria-label="Minimum Rest Period"
          unit="hrs"
          min={0}
          step={0.25}
          size="xs"
          className="text-right"
          {...defaultBreakInputProps}
        />
      </div>
      <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
        {currentLabels.description}
      </p>
    </div>
  );
};
